/**
 * The open screen's title — the same in every layout: its icon, its TITLE
 * (never the object name or type), a small ⓘ whose tooltip (hover AND focus)
 * names the object and its type for the people who need it, the pin star, the
 * host's extras, full screen and close.
 */
import { Info, Maximize2, Minimize2, Star, X } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../navIcons'
import { S, iconButton } from '../tokens'
import type { RunNavItem } from '../types'
import { RUN_TYPE_LABEL } from '../typeLabels'

export function InfoTip({ item, align = 'left' }: { item: RunNavItem; align?: 'left' | 'right' }) {
  const kind = RUN_TYPE_LABEL[item.obj_type] || item.obj_type
  return (
    <span className="tw-sh-info" style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        type="button"
        className="tw-sh-btn"
        aria-label={`Screen details: ${item.obj_name}, ${kind}`}
        {...tw('action', 'shell-screen-info')}
        style={{ ...iconButton(26), borderRadius: '50%' }}
      >
        <Info size={15} aria-hidden />
      </button>
      <span
        className="tw-sh-tip"
        role="tooltip"
        style={{
          position: 'absolute', top: 'calc(100% + 6px)', [align]: -6, zIndex: 60,
          whiteSpace: 'nowrap', padding: '8px 12px', borderRadius: 8,
          background: S.text, color: S.panel, fontSize: 13, lineHeight: 1.55,
          boxShadow: '0 6px 18px rgba(0,0,0,0.22)', fontWeight: 400,
        } as CSSProperties}
      >
        <span style={{ display: 'block' }}>Object: <b>{item.obj_name}</b></span>
        <span style={{ display: 'block' }}>Type: {kind}</span>
      </span>
    </span>
  )
}

export function PinButton({ pinned, onToggle, compact = false }: {
  pinned: boolean; onToggle: () => void; compact?: boolean
}) {
  const label = pinned ? 'Unpin this screen' : 'Pin this screen'
  return (
    <button
      type="button"
      className="tw-sh-btn"
      onClick={onToggle}
      aria-pressed={pinned}
      aria-label={label}
      title={label}
      {...tw('action', 'shell-pin', { pinned })}
      style={compact ? iconButton(34) : {
        display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 10px',
        border: `1px solid ${S.border}`, borderRadius: S.radius, background: 'transparent',
        color: S.text2, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
      }}
    >
      <Star size={16} aria-hidden fill={pinned ? S.star : 'none'} color={pinned ? S.starStroke : 'currentColor'} />
      {!compact && (pinned ? 'Pinned' : 'Pin')}
    </button>
  )
}

export interface ScreenTitleProps {
  item: RunNavItem
  pinned: boolean
  onTogglePin: () => void
  fullScreen: boolean
  onToggleFullScreen: () => void
  onClose?: () => void
  /** Host extras (the builder's refresh). */
  extras?: ReactNode
  /** Leading control (the classic "show navigation" hamburger). */
  leading?: ReactNode
  /** Rendered under / beside the title (a breadcrumb). */
  subtitle?: ReactNode
  height?: number
  compact?: boolean
  /** Draw as a bar (border + background) or inline. */
  bar?: boolean
  style?: CSSProperties
}

export function ScreenTitle({
  item, pinned, onTogglePin, fullScreen, onToggleFullScreen, onClose, extras, leading, subtitle,
  height = 56, compact = false, bar = true, style,
}: ScreenTitleProps) {
  return (
    <div
      {...tw('screen', `shell-title:${item.obj_name}`)}
      style={{
        height, boxSizing: 'border-box', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: compact ? '0 0.75rem' : '0 1rem',
        ...(bar ? { borderBottom: `1px solid ${S.border}`, background: S.panel } : {}),
        ...style,
      }}
    >
      {leading}
      <span aria-hidden style={{
        width: compact ? 28 : 34, height: compact ? 28 : 34, borderRadius: 9, flexShrink: 0,
        background: S.accentWeak, color: S.accent,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <NavIcon node={item} size={compact ? 15 : 18} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <h1 className="tw-sh-clip" style={{
            margin: 0, fontSize: compact ? 15 : 16.5, fontWeight: 600, color: S.text, minWidth: 0,
          }}>
            {item.title}
          </h1>
          <InfoTip item={item} />
        </div>
        {subtitle}
      </div>
      {extras}
      <PinButton pinned={pinned} onToggle={onTogglePin} compact={compact} />
      <button
        type="button"
        className="tw-sh-btn"
        onClick={onToggleFullScreen}
        aria-pressed={fullScreen}
        aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
        title={fullScreen ? 'Exit full screen (Esc)' : 'Full screen'}
        {...tw('action', fullScreen ? 'shell-exit-fullscreen' : 'shell-fullscreen')}
        style={fullScreen ? {
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px',
          border: `1px solid ${S.border}`, borderRadius: S.radius, background: 'transparent',
          color: S.text, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
        } : iconButton(34)}
      >
        {fullScreen ? <><Minimize2 size={15} aria-hidden /> Exit full screen</> : <Maximize2 size={16} aria-hidden />}
      </button>
      {onClose && (
        <button
          type="button"
          className="tw-sh-btn"
          onClick={onClose}
          aria-label={`Close ${item.title}`}
          title="Close"
          {...tw('action', 'close_object')}
          style={iconButton(34)}
        >
          <X size={17} aria-hidden />
        </button>
      )}
    </div>
  )
}

/** A breadcrumb of the folders a screen sits in, with icons. Container crumbs
 *  (folders, websites) are buttons when the layout can show one (`onFolder`),
 *  else plain text. */
export function Breadcrumb({ path, onFolder, current }: {
  path: RunNavItem[]
  onFolder?: (folder: RunNavItem) => void
  current?: RunNavItem | null
}) {
  if (!path.length && !current) return null
  const crumbs = current ? [...path, current] : path
  return (
    <nav aria-label="Breadcrumb" style={{ minWidth: 0 }}>
      <ol style={{
        display: 'flex', alignItems: 'center', gap: 4, margin: 0, padding: 0, listStyle: 'none',
        fontSize: 13, color: S.text2, minWidth: 0, overflow: 'hidden',
      }}>
        {crumbs.map((c, i) => {
          // Only the screen itself is the current page; a path-only breadcrumb
          // keeps every folder clickable.
          const last = !!current && i === crumbs.length - 1
          return (
            <li key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
              {i > 0 && <span aria-hidden style={{ color: S.faint }}>›</span>}
              {onFolder && !last && (c.obj_type === '-' || c.obj_type === 'X') ? (
                <button type="button" className="tw-sh-btn" onClick={() => onFolder(c)}
                  {...tw('action', 'shell-crumb', { id: c.id })}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px',
                    border: 'none', background: 'transparent', color: S.text2, cursor: 'pointer',
                    fontSize: 13, fontFamily: 'inherit', borderRadius: 4, minWidth: 0,
                  }}>
                  <NavIcon node={c} size={13} />
                  <span className="tw-sh-clip">{c.title}</span>
                </button>
              ) : (
                <span aria-current={last ? 'page' : undefined} className="tw-sh-clip"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0,
                    color: last ? S.text : S.text2, fontWeight: last ? 500 : 400 }}>
                  <NavIcon node={c} size={13} />
                  <span className="tw-sh-clip">{c.title}</span>
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
